import { useLocation, useParams } from 'react-router-dom';
import { useContext, useEffect, useState } from 'react';
import { ROLE, Product } from '../interface/interface';
import { useAuth } from 'react-oidc-context';
import Loading from '../components/Loading';
import ProductCarousel from '../components/ProductCarousel';
import RenderProductDescription from '../components/RenderProductDescription';
import renderMediaUrls from '../utils/getMediaUrls';
import AddToCartButton from '../components/AddToCartButton';
import GuestAddToCartButton from '../components/GuestAddToCartButton';
import { GuestCartProvider } from '../context/guestCartContext';
import SimilarProducts from './SimilarProduct.tsx';
import ProductDescriptionTabs from './ProductDescriptionTab.tsx';
import {
  CheckIconNew,
  OutOfStockIcon,
  WishlistHeartIcon,
} from '../assets/svgImages';
import { WishListContext } from '../context/wishListContext.tsx';
import Accordion from './Accordion.tsx';
import Breadcrumbs from './Breadcrumbs.tsx';
import { UserContext } from '../context/userContext.tsx';
import ShopifyRichText from './RenderRichTextContent.tsx';
import { useProductDetails } from '../hooks/useProductDetails.ts';
import { useWishlist } from '../hooks/useWishlist.ts';
import ImageZoomModal from '../helpers/ImageZoomModal.tsx';
import { buildPrescriptionDetails } from '../utils/buildPrescriptionDetails.ts';
import { buildProductInfo } from '../utils/buildProductInfo.ts';
import { parseJson } from '../utils/parseJson.ts';

interface ProductDetailsProps {
  isGuest?: boolean;
}

const PatientProductDetails = ({ isGuest = false }: ProductDetailsProps) => {
  const { productId } = useParams();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const { callWishList, wishListData } = useContext(WishListContext);
  const { user } = useAuth();
  const { isMobile } = useContext(UserContext);
  const isPatient = (user?.profile?.groups as Array<string>)?.includes(
    ROLE.Patients,
  );

  const { product, prescription, similarProduct, loading } = useProductDetails(
    productId,
    isGuest,
  );
  const { isWishListed, toggleWishlist, wishlistLoading } = useWishlist(
    product?.id,
    wishListData,
    callWishList,
  );

  useEffect(() => {
    setOpenIndex(isMobile ? 0 : null);
  }, [isMobile]);

  const handleToggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  const direction = parseJson(product?.direction);

  const tabs = [
    {
      key: 'tab1',
      label: 'General Information',
      content: (
        <div className="mb-3 text-sm font-regular text-left">
          <RenderProductDescription description={product?.description} />
        </div>
      ),
      needShown: true,
    },
    {
      key: 'tab2',
      label: 'Ingredients',
      content: (
        <div className="my-3">
          {product?.ingredients ? (
            <ShopifyRichText value={JSON.parse(product.ingredients)} />
          ) : (
            <div className="text-sm font-regular text-primary-blue-500 text-left">
              No Ingredients data available
            </div>
          )}
        </div>
      ),
      needShown: true,
    },
    {
      key: 'tab3',
      label: 'Directions',
      content: (
        <div className="my-3">
          {direction ? (
            <div className="text-sm font-regular text-left">
              <ShopifyRichText value={direction} />
            </div>
          ) : (
            <div className="text-sm font-regular text-primary-blue-500 text-left">
              No Directions data available
            </div>
          )}
        </div>
      ),
      needShown: true,
    },
    {
      key: 'tab4',
      label: 'Warnings',
      content: (
        <div className="my-3">
          {product?.warnings ? (
            <ShopifyRichText value={JSON.parse(product.warnings)} />
          ) : (
            <div className="text-sm font-regular text-primary-blue-500 text-left">
              No Warnings data available
            </div>
          )}
        </div>
      ),
      needShown: true,
    },
  ];

  const accordionTabs = [
    {
      key: 'accordion1',
      label: 'Prescription Details',
      content: (
        <div className="text-sm text-gray-700">
          {product?.prescription_required &&
            !location?.state?.isSubstitute &&
            !location?.state?.nonOrderable &&
            isPatient && (
              <div
                className="col-span-1 xl:col-span-2 border-b rounded-l-md
                px-3.5 xl:px-7 py-3 flex flex-col justify-between shrink"
              >
                {buildPrescriptionDetails(prescription).map(
                  ({ label, value }) => (
                    <div
                      key={label}
                      className="flex justify-between items-center border-b border-gray-100 py-2"
                    >
                      <span className="text-sm font-semibold text-primary-blue-500">
                        {label}
                      </span>
                      <span className="text-sm text-gray-700 text-right break-words whitespace-normal">
                        {value || '-'}
                      </span>
                    </div>
                  ),
                )}
              </div>
            )}
        </div>
      ),
      needShown: Boolean(prescription) && !location?.state?.nonOrderable,
    },
    {
      key: 'accordion2',
      label: 'Product Information',
      content: (
        <div className="text-sm text-gray-700">
          {product?.id && (
            <div className="flex flex-col gap-2">
              {buildProductInfo(product).map(({ label, value }) => (
                <div
                  key={label}
                  className="flex justify-between items-center border-b border-gray-100 py-2"
                >
                  <span className="text-sm font-semibold text-primary-blue-500">
                    {label}
                  </span>
                  <span className="text-sm text-gray-700 leading-6 text-right sm:break-words sm:whitespace-normal">
                    {value || '-'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ),
      needShown: Boolean(prescription),
    },
  ];

  if (loading || wishlistLoading) return <Loading />;

  return (
    <div className="mx-auto container-compact">
      <Breadcrumbs
        data={[
          {
            tag: 'a',
            href:
              location?.state?.isNonScriptPage || isMobile
                ? '/non-script-range'
                : '/home',
            children: <>Home</>,
          },
          { tag: 'p', children: <span>Product Details</span> },
        ]}
      />

      {product ? (
        <>
          <div className="bg-white rounded-lg border border-grey-200 shadow-normal p-4 md:p-6">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start">
              <div className="w-full md:w-1/2 flex justify-center self-start md:sticky top-4">
                <ProductCarousel
                  media={renderMediaUrls(product)}
                  setImageUrl={setImageUrl}
                  setOpen={setOpen}
                />
              </div>

              <div className="w-full md:w-1/2 flex flex-col">
                {!product?.prescription_required && (
                  <div className="flex justify-end mb-3">
                    <button
                      type="button"
                      onClick={() => toggleWishlist()}
                      aria-label="Add to wishlist"
                      className="w-9 h-9 shrink-0 flex items-center justify-center rounded border-2 border-secondary-pink-300 rounded-full"
                    >
                      <WishlistHeartIcon
                        className={
                          isWishListed
                            ? 'fill-pink-400 stroke-pink-400'
                            : 'fill-white stroke-pink-400'
                        }
                      />
                    </button>
                  </div>
                )}
                <p className="text-sm font-semibold uppercase text-secondary-cornflowerblue-600 mb-1">
                  {product.product_brand}
                </p>
                <h1 className="text-xl md:text-2xl font-semibold text-primary-blue-500 leading-tight mb-3">
                  {product.name}
                </h1>
                <div className="flex flex-wrap gap-2 mb-6">
                  {product?.productCategory && (
                    <span className="px-3 py-1 rounded-full uppercase text-xs text-white font-normal bg-secondary-pink-500">
                      {product.productCategory.name}
                    </span>
                  )}
                  {product?.productFormulation &&
                    product.productCategory?.name !==
                      product.productFormulation.name && (
                      <span className="px-3 py-1 rounded-full uppercase text-xs text-white font-normal bg-secondary-green-600 whitespace-nowrap">
                        {product.productFormulation.name}
                      </span>
                    )}
                </div>
                <hr className="border-gray-100 mb-4 md:mb-6 2xl:mb-8" />
                <div className="mb-6 md:mb-8 xl:mb-10">
                  <div className="flex flex-wrap items-baseline gap-2">
                    {product?.old_price && (
                      <span className="text-sm font-semibold text-primary-blue-500 line-through italic">
                        ${product.old_price}
                      </span>
                    )}
                    <span className="text-xl md:text-2xl font-semibold text-primary-blue-500">
                      ${product?.price}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    {product.inventory_quantity > 0 ? (
                      <>
                        <CheckIconNew className="w-5 h-5" />
                        <p className="text-xs font-semibold text-secondary-green-600">
                          In stock and ready to ship
                        </p>
                      </>
                    ) : (
                      <>
                        <OutOfStockIcon className="w-5 h-5 fill-secondary-pink-500" />
                        <p className="text-xs font-semibold text-secondary-pink-500">
                          Product is Out of Stock
                        </p>
                      </>
                    )}
                  </div>
                </div>
                {isPatient && (
                  <div className="flex items-center justify-between md:justify-start">
                    <div className="w-full xl:w-8/12 rounded-xl">
                      <AddToCartButton
                        product={product}
                        maxQty={location?.state?.totalQty}
                        substituteProduct={location?.state?.substituteProduct}
                        nonOrderable={location?.state?.nonOrderable}
                      />
                    </div>
                  </div>
                )}
                {isGuest && (
                  <div className="w-full">
                    <GuestCartProvider>
                      <GuestAddToCartButton
                        product={product}
                        substituteProduct={product.substituteProduct}
                      />
                    </GuestCartProvider>
                  </div>
                )}
                {!isGuest && !isPatient && (
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-semibold text-primary-blue-500">
                      Stock on hand
                    </p>
                    <p className="text-sm text-gray-700">
                      {product?.inventory_quantity ?? '-'} units available
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 md:mt-8 hidden md:block">
              <ProductDescriptionTabs tabs={tabs} isLoading={loading} />
            </div>

            <div className="mt-6 md:mt-8 md:hidden block">
              {[...tabs, ...accordionTabs]
                .filter((tab) => tab.needShown)
                .map((tab, index) => (
                  <Accordion
                    key={tab.key}
                    title={
                      <p className="text-sm font-semibold text-primary-blue-400">
                        {tab.label}
                      </p>
                    }
                    isOpen={openIndex === index}
                    onAccordionToggle={() => handleToggle(index)}
                  >
                    <>{tab.content}</>
                  </Accordion>
                ))}
            </div>

            <ImageZoomModal
              isOpen={open}
              imageUrl={imageUrl}
              onClose={() => setOpen(false)}
            />
          </div>

          <div className="mt-4 hidden md:block">
            {accordionTabs
              .filter((tab) => tab.needShown)
              .map((tab, index) => (
                <Accordion
                  key={tab.key}
                  title={
                    <p className="text-sm font-semibold text-primary-blue-400">
                      {tab.label}
                    </p>
                  }
                  isOpen={openIndex === index}
                  onAccordionToggle={() => handleToggle(index)}
                >
                  <>{tab.content}</>
                </Accordion>
              ))}
          </div>

          {!!similarProduct?.length && (
            <section className="mt-8 md:mt-10 lg:mt-14 pb-12 md:pb-0">
              <SimilarProducts
                products={similarProduct?.filter(
                  (p: Product) => p.id !== product.id,
                )}
                isGuest={isGuest}
                substituteProduct={product?.substituteProduct}
              />
            </section>
          )}
        </>
      ) : (
        <h3 className="text-base font-semibold text-center leading-7 text-gray-900 px-4">
          Product Information does not exist
        </h3>
      )}
    </div>
  );
};

export default PatientProductDetails;
